import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  fromEvent,
  map,
  of,
  retry,
  startWith,
  switchMap,
  takeUntil,
  tap,
  timer
} from 'rxjs';

import { ActiveMaintenanceNoticeDTO, SystemMaintenanceControllerService } from '@chicilon/angular-user-service';
import { MaintenanceBroadcastMessage, MaintenanceBroadcastService } from '@core/services/maintenance-broadcast.service';
import { MaintenanceStorageService } from '@core/services/maintenance-storage.service';
import { login, loginSuccess, logout } from '@state/auth/auth.actions';
import {
  dismissMaintenanceNotice,
  loadMaintenanceNoticeSuccess,
  setMaintenanceNoticeDismissedState
} from './system.actions';

const POLL_INTERVAL_MS = 10 * 60 * 1000;
const STALE_THRESHOLD_MS = 5 * 60 * 1000;
const FETCH_RETRY_COUNT = 6;
const FETCH_RETRY_DELAY_MS = 10000;

@Injectable()
export class SystemEffects {
  actions$ = inject(Actions);
  private systemMaintenanceControllerService = inject(SystemMaintenanceControllerService);
  private maintenanceStorageService = inject(MaintenanceStorageService);
  private broadcast = inject(MaintenanceBroadcastService);

  private lastFetchAt = 0;

  // Poll only while the tab is visible so background tabs pause and only the foreground one hits the API.
  pollMaintenanceNotice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginSuccess),
      switchMap(() =>
        this.tabVisible$().pipe(
          switchMap(isVisible => (isVisible ? timer(this.msUntilDue(), POLL_INTERVAL_MS) : EMPTY)),
          takeUntil(this.actions$.pipe(ofType(logout)))
        )
      ),
      switchMap(() => this.fetchActiveNotice())
    )
  );

  syncNoticeAcrossTabs$ = createEffect(() =>
    this.broadcast.messages$.pipe(
      filter(
        (message): message is Extract<MaintenanceBroadcastMessage, { type: 'notice' }> => message.type === 'notice'
      ),
      // Fresh data pushed by a sibling tab counts as a fetch, so becoming visible soon after won't refetch.
      tap(() => (this.lastFetchAt = Date.now())),
      map(message => loadMaintenanceNoticeSuccess({ notice: message.notice }))
    )
  );

  syncDismissAcrossTabs$ = createEffect(() =>
    this.broadcast.messages$.pipe(
      filter(message => message.type === 'dismissed' || message.type === 'dismiss-cleared'),
      map(message => setMaintenanceNoticeDismissedState({ dismissed: message.type === 'dismissed' }))
    )
  );

  clearDismissedOnFreshLogin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(login),
        tap(() => {
          this.maintenanceStorageService.clearDismissed();
          this.broadcast.post({ type: 'dismiss-cleared' });
          // Fresh sign-in starts a clean slate, so the first poll fires immediately, not mid-interval.
          this.lastFetchAt = 0;
        })
      ),
    { dispatch: false }
  );

  hydrateDismissedFlag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginSuccess),
      map(() => setMaintenanceNoticeDismissedState({ dismissed: this.maintenanceStorageService.isDismissed() }))
    )
  );

  persistDismissedNotice$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(dismissMaintenanceNotice),
        tap(() => {
          this.maintenanceStorageService.setDismissed();
          this.broadcast.post({ type: 'dismissed' });
        })
      ),
    { dispatch: false }
  );

  private tabVisible$() {
    return fromEvent(document, 'visibilitychange').pipe(
      startWith(null),
      map(() => document.visibilityState === 'visible'),
      distinctUntilChanged()
    );
  }

  // Skip the refetch when returning to a tab whose data is still fresh (younger than the stale threshold).
  private msUntilDue(): number {
    return Math.max(0, this.lastFetchAt + STALE_THRESHOLD_MS - Date.now());
  }

  private fetchActiveNotice() {
    return this.systemMaintenanceControllerService.getActiveNotice().pipe(
      retry({ count: FETCH_RETRY_COUNT, delay: FETCH_RETRY_DELAY_MS }),
      map(res => res.data ?? null),
      tap(notice => {
        if (notice) {
          this.maintenanceStorageService.setCachedNotice(notice);
        } else {
          this.maintenanceStorageService.clearCachedNotice();
        }

        this.broadcast.post({ type: 'notice', notice });
      }),
      map(notice => loadMaintenanceNoticeSuccess({ notice })),
      // Falling back to the cached notice during an outage keeps the banner up exactly when it matters most.
      catchError(() => of(loadMaintenanceNoticeSuccess({ notice: this.getValidCachedNotice() }))),
      tap(() => (this.lastFetchAt = Date.now()))
    );
  }

  private getValidCachedNotice(): ActiveMaintenanceNoticeDTO | null {
    const cached = this.maintenanceStorageService.getCachedNotice();

    if (!cached?.end_at) return null;

    return new Date(cached.end_at).getTime() >= Date.now() ? cached : null;
  }
}
